import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, AlertCircle } from 'lucide-react';
import { Card } from '../BossUI';
import { Alert, AlertTitle, AlertDescription } from '../ui/alert';
import { retryOn500 } from '../../utils/retryOn500';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('wmf_token')}`,
});

// Modal component for adding/editing a retreat
const RetreatModal = ({ retreat, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: retreat?.name || '',
    startDate: retreat?.startDate || '',
    endDate: retreat?.endDate || '',
  });
  const [validationError, setValidationError] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.endDate < formData.startDate) {
      setValidationError('End date must be on or after start date');
      return;
    }
    setValidationError(null);
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            {retreat ? 'Edit Songwriting Retreat' : 'Add Songwriting Retreat'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Spring 2026 Nashville Retreat"
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">Start Date</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">End Date</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2"
                  required
                />
              </div>
            </div>

            {validationError && (
              <p className="text-sm text-red-600">{validationError}</p>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : retreat ? 'Update' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const ManageRetreats = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRetreat, setSelectedRetreat] = useState(null);
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['retreats', 'all'],
    queryFn: async () => {
      const response = await retryOn500({
        requestFn: () => fetch(`${process.env.REACT_APP_API_URL}/retreats?activeOnly=false`, {
          headers: authHeaders(),
        }),
      });
      if (!response.ok) throw new Error('Failed to fetch retreats');
      return response.json();
    },
  });

  const addRetreat = useMutation({
    mutationFn: async (formData) => {
      const response = await retryOn500({
        requestFn: () => fetch(`${process.env.REACT_APP_API_URL}/retreats`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(formData),
        }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.msg || 'Failed to add retreat');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['retreats'] });
      setModalOpen(false);
      setError(null);
    },
    onError: (err) => setError(err.message),
  });

  const updateRetreat = useMutation({
    mutationFn: async ({ id, ...formData }) => {
      const response = await retryOn500({
        requestFn: () => fetch(`${process.env.REACT_APP_API_URL}/retreats/${id}`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify(formData),
        }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.msg || 'Failed to update retreat');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['retreats'] });
      setModalOpen(false);
      setError(null);
    },
    onError: (err) => setError(err.message),
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, isActive }) => {
      const response = await retryOn500({
        requestFn: () => fetch(`${process.env.REACT_APP_API_URL}/retreats/${id}`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ isActive }),
        }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.msg || 'Failed to update status');
      }
      return response.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['retreats'] }),
    onError: (err) => setError(err.message),
  });

  const handleSubmit = (formData) => {
    if (selectedRetreat) {
      updateRetreat.mutate({ id: selectedRetreat.id, ...formData });
    } else {
      addRetreat.mutate(formData);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  const retreats = data?.retreats || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Manage Songwriting Retreats</h2>
          <p className="mt-1 text-sm text-slate-500">
            Retreats added here appear in the Songwriting Assessment form and in per-retreat invoicing.
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedRetreat(null);
            setModalOpen(true);
          }}
          className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          <CalendarPlus className="h-4 w-4 mr-2" />
          Add Retreat
        </button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Start Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">End Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Active</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {retreats.map((retreat) => (
                <tr key={retreat.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{retreat.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{retreat.startDate}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{retreat.endDate}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={retreat.isActive ? 'active' : 'inactive'}
                      onChange={(e) => toggleActive.mutate({ id: retreat.id, isActive: e.target.value === 'active' })}
                      className="text-sm border border-slate-300 rounded-md p-1"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedRetreat(retreat);
                        setModalOpen(true);
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {retreats.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-sm text-slate-400">
                    No songwriting retreats yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {modalOpen && (
        <RetreatModal
          retreat={selectedRetreat}
          onClose={() => {
            setModalOpen(false);
            setSelectedRetreat(null);
          }}
          onSubmit={handleSubmit}
          isLoading={addRetreat.isLoading || updateRetreat.isLoading}
        />
      )}
    </div>
  );
};

export default ManageRetreats;
